/**
 * TwinReadinessCard (iter1068) — „Какво задвижва близнаците".
 * Показва всеки близнак от каталога със състояние (с оценка / чака данни),
 * точно какво липсва и бутон, който води направо до въвеждането.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, CircleDashed, Network, ArrowRight } from "lucide-react";
import { TWIN_CATALOG, TWIN_IDS } from "@/lib/twinCatalog";

const band = (s) =>
  s >= 80 ? "отлично" : s >= 65 ? "добро" : s >= 50 ? "средно" : "за внимание";

export default function TwinReadinessCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    axios.get(`${API}/health-twin/twin-score`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [API]);

  const rows = useMemo(() => {
    const sub = data?.sub_scores || {};
    const applicable = data?.twins_all?.length ? data.twins_all : TWIN_IDS;
    return applicable
      .filter((id) => TWIN_CATALOG[id])
      .map((id) => ({ id, score: sub[id] ?? null, ...TWIN_CATALOG[id] }))
      .sort((a, b) => (a.score === null ? -1 : 1) - (b.score === null ? -1 : 1));
  }, [data]);

  const active = rows.filter((r) => r.score !== null).length;
  const waiting = rows.filter((r) => r.score === null);
  const pct = rows.length ? Math.round((active / rows.length) * 100) : 0;
  const visible = showAll ? rows : [...waiting, ...rows.filter((r) => r.score !== null)].slice(0, 8);

  const card = isDark
    ? "bg-slate-900/60 border border-slate-800"
    : "bg-white border border-slate-200";

  if (loading) {
    return (
      <div className={`rounded-2xl p-5 ${card}`} data-testid="twin-readiness-loading">
        <Loader2 className="w-4 h-4 animate-spin opacity-60" />
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-5 ${card}`} data-testid="twin-readiness-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className={`text-[11px] uppercase tracking-wider font-semibold ${
            isDark ? "text-indigo-300" : "text-indigo-700"}`}>
            Мрежа от близнаци
          </div>
          <h2 className="text-base md:text-lg font-bold mt-0.5">Какво задвижва близнаците</h2>
          <p className={`text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            Всеки близнак се задвижва от определени данни. Тук виждате кои вече
            имат оценка и какво липсва на останалите.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-bold" data-testid="twin-readiness-count">
            {active}/{rows.length}
          </div>
          <div className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            задвижени близнаци
          </div>
        </div>
      </div>

      <div className="mt-3 h-2 rounded-full overflow-hidden bg-slate-200 dark:bg-white/10">
        <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-all"
          style={{ width: `${pct}%` }} data-testid="twin-readiness-bar" />
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5"
        data-testid="twin-readiness-list">
        {visible.map((r) => {
          const ready = r.score !== null;
          return (
            <div key={r.id} data-testid={`twin-readiness-row-${r.id}`}
              className={`rounded-xl p-3 border ${
                ready
                  ? isDark ? "border-slate-800 bg-slate-950/40" : "border-slate-200 bg-slate-50"
                  : isDark ? "border-amber-900/60 bg-amber-950/20" : "border-amber-200 bg-amber-50"
              }`}>
              <div className="flex items-center gap-2 min-w-0">
                {ready
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  : <CircleDashed className="w-4 h-4 text-amber-500 shrink-0" />}
                <span className="text-sm font-semibold truncate">{r.label}</span>
                {ready && (
                  <span className={`ml-auto text-xs font-bold shrink-0 ${
                    isDark ? "text-slate-300" : "text-slate-700"}`}>
                    {r.score}
                  </span>
                )}
              </div>
              <p className={`text-xs mt-1.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {ready ? `Оценка ${r.score} от 100 — ${band(r.score)}.` : r.action_bg}
              </p>
              {!ready && (
                <Button size="sm" variant="outline" className="mt-2 h-7 text-xs"
                  data-testid={`twin-readiness-action-${r.id}`}
                  onClick={() => navigate(r.action_route)}>
                  Въведете сега <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {rows.length > visible.length && (
          <Button size="sm" variant="ghost" data-testid="twin-readiness-show-all"
            onClick={() => setShowAll(true)}>
            Покажете всички {rows.length}
          </Button>
        )}
        <Button size="sm" variant="outline" data-testid="twin-readiness-open-network"
          onClick={() => navigate("/twins/network")}>
          <Network className="w-3.5 h-3.5 mr-1.5" /> Отворете мрежата
        </Button>
      </div>
    </div>
  );
}
